import { CalendarBlank, CaretDown, CaretRight, Check, Minus } from '@phosphor-icons/react';
import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Selector jerárquico de periodos Año → Mes → Día con checkboxes tri-estado
 * (estilo slicer de Power BI). Permite multi-selección de periodos NO contiguos
 * (ej. mayo 2025 + mayo 2026), excluir meses sueltos, marcar años completos, etc.
 *
 * Representación de la selección (compacta) hacia el contexto global:
 * - `aniosSel`: años completos.
 * - `mesesSel`: meses completos ('YYYY-MM') cuyo año no está completo.
 * - `diasSel`: días sueltos ('YYYY-MM-DD').
 * Vacío en los tres = sin filtro de fecha (todos los periodos).
 *
 * Internamente trabaja con el conjunto de DÍAS seleccionados (derivado del
 * catálogo de fechas disponibles) para que el tri-estado sea consistente, y
 * "comprime" ese conjunto a la representación mínima al emitir el cambio.
 */

const NOMBRES_MES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

type Estado = 'all' | 'some' | 'none';

interface SeleccionPeriodos {
  aniosSel: number[];
  mesesSel: string[];
  diasSel: string[];
}

interface FiltroPeriodosProps {
  /** Fechas disponibles 'YYYY-MM-DD' (catálogo de fact_costos). */
  fechasDisponibles: string[];
  aniosSel: number[];
  mesesSel: string[];
  diasSel: string[];
  onChange: (sel: SeleccionPeriodos) => void;
}

const mesKey = (anio: number, mes: number) => `${anio}-${String(mes).padStart(2, '0')}`;

/** Lunes (inicio de semana ISO) de la fecha 'YYYY-MM-DD', como 'YYYY-MM-DD'. */
function lunesISO(iso: string): string {
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7));
  const d = Number(iso.slice(8, 10));
  const fecha = new Date(Date.UTC(y, m - 1, d));
  const dow = fecha.getUTCDay(); // 0=dom..6=sáb
  const diff = dow === 0 ? -6 : 1 - dow; // retroceder al lunes
  fecha.setUTCDate(fecha.getUTCDate() + diff);
  return fecha.toISOString().slice(0, 10);
}
/** Etiqueta de la semana dentro del mes: "Sem 9–15" (rango de días del mes). */
function etiquetaSemana(diasDelMes: string[]): string {
  if (diasDelMes.length === 0) return 'Semana';
  const d1 = Number(diasDelMes[0]!.slice(8, 10));
  const d2 = Number(diasDelMes[diasDelMes.length - 1]!.slice(8, 10));
  return d1 === d2 ? `Sem ${d1}` : `Sem ${d1}–${d2}`;
}

function resumenTexto(aniosSel: number[], mesesSel: string[], diasSel: string[]): string {
  const tokens = [...aniosSel.map((a) => String(a)), ...mesesSel, ...diasSel];
  if (tokens.length === 0) return 'Todos';
  if (tokens.length <= 2) return tokens.join(', ');
  return `${tokens.slice(0, 2).join(', ')} +${tokens.length - 2}`;
}

export function FiltroPeriodos({
  fechasDisponibles,
  aniosSel,
  mesesSel,
  diasSel,
  onChange,
}: FiltroPeriodosProps) {
  const [abierto, setAbierto] = useState(false);
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!abierto) return;
    const onClickFuera = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', onClickFuera);
    return () => document.removeEventListener('mousedown', onClickFuera);
  }, [abierto]);

  // Catálogo: años (desc), meses por año (asc), días por mes/año, y semanas
  // ISO (lun-dom) dentro de cada mes con sus días.
  const cat = useMemo(() => {
    const anios: number[] = [];
    const mesesPorAnio = new Map<number, number[]>();
    const diasPorMes = new Map<string, string[]>();
    const diasPorAnio = new Map<number, string[]>();
    // Semana ISO dentro del mes: clave 'YYYY-MM|<lunesISO>'. Una misma semana
    // que cruza meses queda partida (un grupo por mes), como pediste.
    const semanasPorMes = new Map<string, string[]>(); // mk -> [semKey...] (orden)
    const diasPorSemana = new Map<string, string[]>(); // semKey -> [dias]
    for (const d of [...fechasDisponibles].sort()) {
      const anio = Number(d.slice(0, 4));
      const mes = Number(d.slice(5, 7));
      const mk = d.slice(0, 7);
      if (!mesesPorAnio.has(anio)) {
        mesesPorAnio.set(anio, []);
        diasPorAnio.set(anio, []);
        anios.push(anio);
      }
      const mlist = mesesPorAnio.get(anio)!;
      if (!mlist.includes(mes)) mlist.push(mes);
      if (!diasPorMes.has(mk)) diasPorMes.set(mk, []);
      diasPorMes.get(mk)!.push(d);
      diasPorAnio.get(anio)!.push(d);
      // Semana ISO dentro del mes (la clave incluye el mes para partir las que cruzan).
      const semKey = `${mk}|${lunesISO(d)}`;
      if (!semanasPorMes.has(mk)) semanasPorMes.set(mk, []);
      const slist = semanasPorMes.get(mk)!;
      if (!slist.includes(semKey)) slist.push(semKey);
      if (!diasPorSemana.has(semKey)) diasPorSemana.set(semKey, []);
      diasPorSemana.get(semKey)!.push(d);
    }
    anios.sort((a, b) => b - a);
    for (const m of mesesPorAnio.values()) m.sort((a, b) => a - b);
    return { anios, mesesPorAnio, diasPorMes, diasPorAnio, semanasPorMes, diasPorSemana };
  }, [fechasDisponibles]);

  // Selección como conjunto de días (derivada de la representación gruesa).
  const diasSeleccionados = useMemo(() => {
    const s = new Set<string>();
    for (const a of aniosSel) for (const d of cat.diasPorAnio.get(a) ?? []) s.add(d);
    for (const m of mesesSel) for (const d of cat.diasPorMes.get(m) ?? []) s.add(d);
    for (const d of diasSel) s.add(d);
    return s;
  }, [aniosSel, mesesSel, diasSel, cat]);

  const estadoDe = (dias: string[]): Estado => {
    if (dias.length === 0) return 'none';
    let n = 0;
    for (const d of dias) if (diasSeleccionados.has(d)) n++;
    return n === 0 ? 'none' : n === dias.length ? 'all' : 'some';
  };
  const estadoAnio = (anio: number) => estadoDe(cat.diasPorAnio.get(anio) ?? []);
  const estadoMes = (anio: number, mes: number) =>
    estadoDe(cat.diasPorMes.get(mesKey(anio, mes)) ?? []);
  const estadoSemana = (semKey: string) => estadoDe(cat.diasPorSemana.get(semKey) ?? []);

  // Comprime el conjunto de días a la representación mínima y emite el cambio.
  const emitir = (nuevoSet: Set<string>) => {
    const anios: number[] = [];
    const meses: string[] = [];
    const dias: string[] = [];
    for (const anio of cat.anios) {
      const diasA = cat.diasPorAnio.get(anio) ?? [];
      const selA = diasA.filter((d) => nuevoSet.has(d));
      if (selA.length === 0) continue;
      if (selA.length === diasA.length) {
        anios.push(anio);
        continue;
      }
      for (const mes of cat.mesesPorAnio.get(anio) ?? []) {
        const mk = mesKey(anio, mes);
        const diasM = cat.diasPorMes.get(mk) ?? [];
        const selM = diasM.filter((d) => nuevoSet.has(d));
        if (selM.length === 0) continue;
        if (selM.length === diasM.length) {
          meses.push(mk);
          continue;
        }
        for (const d of selM) dias.push(d);
      }
    }
    onChange({ aniosSel: anios, mesesSel: meses, diasSel: dias });
  };

  const conDias = (dias: string[], incluir: boolean): Set<string> => {
    const s = new Set(diasSeleccionados);
    if (incluir) for (const d of dias) s.add(d);
    else for (const d of dias) s.delete(d);
    return s;
  };

  const toggleAnio = (anio: number) =>
    emitir(conDias(cat.diasPorAnio.get(anio) ?? [], estadoAnio(anio) !== 'all'));
  const toggleMes = (anio: number, mes: number) =>
    emitir(conDias(cat.diasPorMes.get(mesKey(anio, mes)) ?? [], estadoMes(anio, mes) !== 'all'));
  const toggleSemana = (semKey: string) =>
    emitir(conDias(cat.diasPorSemana.get(semKey) ?? [], estadoSemana(semKey) !== 'all'));
  const toggleDia = (d: string) => emitir(conDias([d], !diasSeleccionados.has(d)));

  const limpiar = () => onChange({ aniosSel: [], mesesSel: [], diasSel: [] });

  const toggleExpand = (key: string) =>
    setExpandidos((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });

  const hayFiltro = diasSeleccionados.size > 0;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className={`flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition ${
          hayFiltro
            ? 'border-blue-400 bg-blue-50 text-blue-700'
            : 'border-input bg-background text-foreground hover:bg-zinc-50'
        }`}
        title="Seleccionar periodos (Año/Mes/Día). Permite comparar periodos no contiguos."
      >
        <CalendarBlank className="h-3.5 w-3.5" weight="bold" />
        Periodos
        <span className="max-w-[160px] truncate text-muted-foreground">
          {resumenTexto(aniosSel, mesesSel, diasSel)}
        </span>
        <CaretDown className="h-3 w-3" weight="bold" />
      </button>

      {abierto && (
        <div className="absolute left-0 z-50 mt-1 w-64 rounded-md border border-zinc-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-zinc-100 px-3 py-2 text-xs">
            <span className="font-medium text-muted-foreground">Año / Mes / Semana / Día</span>
            <button type="button" onClick={limpiar} className="text-blue-600 hover:underline">
              Limpiar
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto py-1">
            {cat.anios.length === 0 ? (
              <p className="px-3 py-2 text-xs text-muted-foreground">Sin fechas.</p>
            ) : (
              cat.anios.map((anio) => {
                const anioExp = expandidos.has(String(anio));
                return (
                  <div key={anio}>
                    <Fila
                      nivel={0}
                      estado={estadoAnio(anio)}
                      label={String(anio)}
                      expandible
                      expandido={anioExp}
                      onToggleExpand={() => toggleExpand(String(anio))}
                      onToggleCheck={() => toggleAnio(anio)}
                    />
                    {anioExp &&
                      (cat.mesesPorAnio.get(anio) ?? []).map((mes) => {
                        const mk = mesKey(anio, mes);
                        const mesExp = expandidos.has(mk);
                        return (
                          <div key={mk}>
                            <Fila
                              nivel={1}
                              estado={estadoMes(anio, mes)}
                              label={NOMBRES_MES[mes - 1] ?? String(mes)}
                              expandible
                              expandido={mesExp}
                              onToggleExpand={() => toggleExpand(mk)}
                              onToggleCheck={() => toggleMes(anio, mes)}
                            />
                            {mesExp &&
                              (cat.semanasPorMes.get(mk) ?? []).map((semKey) => {
                                const semExp = expandidos.has(semKey);
                                const diasSem = cat.diasPorSemana.get(semKey) ?? [];
                                return (
                                  <div key={semKey}>
                                    <Fila
                                      nivel={2}
                                      estado={estadoSemana(semKey)}
                                      label={etiquetaSemana(diasSem)}
                                      expandible
                                      expandido={semExp}
                                      onToggleExpand={() => toggleExpand(semKey)}
                                      onToggleCheck={() => toggleSemana(semKey)}
                                    />
                                    {semExp && (
                                      <div className="flex flex-wrap gap-1 px-3 py-1 pl-14">
                                        {diasSem.map((d) => {
                                          const sel = diasSeleccionados.has(d);
                                          return (
                                            <button
                                              type="button"
                                              key={d}
                                              onClick={() => toggleDia(d)}
                                              className={`h-6 w-6 rounded text-[11px] tabular-nums ${
                                                sel
                                                  ? 'bg-blue-600 text-white'
                                                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                                              }`}
                                              title={d}
                                            >
                                              {Number(d.slice(8, 10))}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                        );
                      })}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Fila de Año o Mes: caret de expandir + checkbox tri-estado + label. */
function Fila({
  nivel,
  estado,
  label,
  expandible,
  expandido,
  onToggleExpand,
  onToggleCheck,
}: {
  nivel: number;
  estado: Estado;
  label: string;
  expandible: boolean;
  expandido: boolean;
  onToggleExpand: () => void;
  onToggleCheck: () => void;
}) {
  return (
    <div
      className="flex items-center gap-1 px-3 py-1 text-xs hover:bg-zinc-50"
      style={{ paddingLeft: `${12 + nivel * 16}px` }}
    >
      {expandible ? (
        <button
          type="button"
          onClick={onToggleExpand}
          className="rounded p-0.5 hover:bg-zinc-200"
          title={expandido ? 'Colapsar' : 'Expandir'}
        >
          {expandido ? (
            <CaretDown className="h-3 w-3" weight="bold" />
          ) : (
            <CaretRight className="h-3 w-3" weight="bold" />
          )}
        </button>
      ) : (
        <span className="w-4" />
      )}
      <button type="button" onClick={onToggleCheck} className="flex items-center gap-1.5">
        <span
          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
            estado === 'none'
              ? 'border-zinc-300 bg-white'
              : 'border-blue-600 bg-blue-600 text-white'
          }`}
        >
          {estado === 'all' && <Check className="h-3 w-3" weight="bold" />}
          {estado === 'some' && <Minus className="h-3 w-3" weight="bold" />}
        </span>
        <span className={nivel === 0 ? 'font-medium' : ''}>{label}</span>
      </button>
    </div>
  );
}
